<template #default>
    <form
        v-if="status.detail"
        @submit.prevent="companyID ? requestUpdate() : requestCreate()"
    >
        <b-form-group
            :label="$t('label.logo')"
            label-cols="4"
            label-align="left"
        >
            <ImageUploader
                :key="companyID"
                :multiple="false"
                :showImageTop="true"
                :buttonText="$t('label.uploadX', [$t('label.image')])"
                @images-changed="uploadImage"
                @images-removed="removeImage"
                :initial-images="createForm.image ? [createForm.image] : []"
                ref="coverUploader"
            />
            <div v-if="submitted && !$v.createForm.image.required" class="invalid-feedback d-block">
                {{ $t("message.requiredX", [$t("label.logo")]) }}
            </div>
        </b-form-group>
        <b-form-group
            :label="$t('module.group')"
            label-cols="4"
            label-align="left"
        >
            <multiselect
                v-model="createForm.group"
                deselect-label="Can't remove this value"
                track-by="value"
                label="text"
                :placeholder="$t('message.pleaseSelectX', [$t('module.group')])"
                :options="groupDdl"
                :searchable="true"
                :allow-empty="false"
            >
            </multiselect>
            
            <div v-if="submitted && !$v.createForm.group.required" class="invalid-feedback d-block">
                {{ $t("message.requiredX", [$t("module.group")]) }}
            </div>
        </b-form-group>
        <b-form-group
            :label="$t('label.name')"
            label-cols="4"
            label-align="left"
        >
            <b-form-input
                v-model.trim="createForm.name"
                :class="{ 'is-invalid': submitted && !$v.createForm.name.required }"
            ></b-form-input>
            <div v-if="submitted && !$v.createForm.name.required" class="invalid-feedback d-block">
                {{ $t("message.requiredX", [$t("label.name")]) }}
            </div>
        </b-form-group>
        <b-form-group
            :label="$t('label.displayName')"
            label-cols="4"
            label-align="left"
        >
            <b-form-input
                v-model.trim="createForm.displayName"
                :class="{ 'is-invalid': submitted && !$v.createForm.displayName.required }"
            ></b-form-input>
            <div v-if="submitted && !$v.createForm.displayName.required" class="invalid-feedback d-block">
                {{ $t("message.requiredX", [$t("label.displayName")]) }}
            </div>
        </b-form-group>
        <b-form-group
            :label="$t('label.operatingTime')"
            label-cols="4"
            label-align="left"
        >
            <b-row>
                <b-col>
                    <b-form-timepicker
                        :placeholder="$t('label.startTime')"
                        v-model.trim="createForm.startTime"
                        :class="{ 'is-invalid': submitted && !$v.createForm.startTime.required }"
                        :show-seconds="false"
                        locale="en-GB"
                        :hour-format="24"
                        @input="val => createForm.startTime = val ? val.substring(0,5) : ''"
                    ></b-form-timepicker>
                    <div v-if="submitted && !$v.createForm.startTime.required" class="invalid-feedback d-block">
                        {{ $t("message.requiredX", [$t("label.startTime")]) }}
                    </div>
                </b-col>
                <p class="mt-2 mb-0 py-0">-</p>
                <b-col>
                    <b-form-timepicker
                        :placeholder="$t('label.endTime')"
                        v-model.trim="createForm.endTime"
                        :class="{ 'is-invalid': submitted && !$v.createForm.endTime.required }"
                        :show-seconds="false"
                        locale="en-GB"
                        :hour-format="24"
                        @input="val => createForm.endTime = val ? val.substring(0,5) : ''"
                    ></b-form-timepicker>
                    <div v-if="submitted && !$v.createForm.endTime.required" class="invalid-feedback d-block">
                        {{ $t("message.requiredX", [$t("label.endTime")]) }}
                    </div>
                </b-col>
            </b-row>
        </b-form-group>
        <b-form-group
            :label="$t('label.description')"
            label-cols="4"
            label-align="left"
        >
            <b-form-textarea
                v-model.trim="createForm.operateDescription"
                rows="1"
            ></b-form-textarea>
        </b-form-group>
        <b-form-group
            :label="$t('label.visibleToEveryone')"
            label-cols="4"
            label-align="left"
        >
            <div class="d-flex justify-content-start mt-2 mb-0">
                <b-form-radio
                    inline
                    v-model.trim="createForm.visibleToEveryone"
                    :class="{ 'is-invalid': submitted && !$v.createForm.visibleToEveryone.required }"
                    name="visible-radios"
                    :value="1"
                >
                    {{ $t('label.yes') }}
                </b-form-radio>
                <b-form-radio
                    inline
                    v-model.trim="createForm.visibleToEveryone"
                    :class="{ 'is-invalid': submitted && !$v.createForm.visibleToEveryone.required }"
                    name="visible-radios"
                    :value="0"
                >
                    {{ $t('label.no') }}
                </b-form-radio>
            </div>
            <div v-if="submitted && !$v.createForm.visibleToEveryone.required" class="invalid-feedback d-block">
                {{ $t("message.requiredX", [$t("label.visibleToEveryone")]) }}
            </div>
        </b-form-group>
        <b-form-group
            :label="$t('label.budget')"
            label-cols="4"
            label-align="left"
        >
            <b-form-input
                type="number"
                step="0.01"
                v-model.trim="createForm.budget"
                :class="{ 'is-invalid': submitted && !$v.createForm.budget.required }"
            ></b-form-input>
            <div v-if="submitted && !$v.createForm.budget.required" class="invalid-feedback d-block">
                {{ $t("message.requiredX", [$t("label.budget")]) }}
            </div>
        </b-form-group>
        <b-form-group
            :label="$t('label.hashtag')"
            label-cols="4"
            label-align="left"
        >
            <multiselect
                v-model="createForm.hashtag"
                :options="[]"
                :multiple="true"
                :taggable="true"
                :placeholder="$t('message.pressEnterToAddX', [])"
                :tag-placeholder="$t('message.pressEnterToAddX', [$t('label.hashtag')])"
                :show-options="false"
                :show-no-options="false"
                v-on:tag="addTag"
            />
        </b-form-group>
        <b-form-group
            :label="$t('label.website')"
            label-cols="4"
            label-align="left"
        >
            <b-form-input
                v-model.trim="createForm.website"
                :class="{ 'is-invalid': submitted && !$v.createForm.website.required }"
            ></b-form-input>
            <div v-if="submitted && !$v.createForm.website.required" class="invalid-feedback d-block">
                {{ $t("message.requiredX", [$t("label.website")]) }}
            </div>
        </b-form-group>
        <b-form-group
            :label="$t('label.annualReportX', [$t('label.website')])"
            label-cols="4"
            label-align="left"
        >
            <b-form-input
                v-model.trim="createForm.annualReportWebsite"
                :class="{ 'is-invalid': submitted && !$v.createForm.annualReportWebsite.required }"
            ></b-form-input>
            <div v-if="submitted && !$v.createForm.annualReportWebsite.required" class="invalid-feedback d-block">
                {{ $t("message.requiredX", [$t('label.annualReportX', [$t('label.website')])]) }}
            </div>
        </b-form-group>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end w-100">
            <b-button
                type="button"
                variant="danger"
                class="mr-2"
                @click="$bvModal.hide('company-form')"
            >{{ $t('label.cancel') }}</b-button>
            <b-button
                type="submit"
                variant="info"
                :disabled="!status.save"
            >{{ companyID ? $t('label.updateX', []) : $t('label.createX', []) }}</b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center align-items-center m-5 p-5">
        <b-spinner class="m-5" variant="info" role="status"></b-spinner>
    </div>
</template>
<script>
import { lookupMethods, companyMethods } from "@/state/helpers";
import { required } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { COMPANY_MODULE } from '@/state/modules/company';
import ImageUploader from '@/components/upload-image';
import Multiselect from "vue-multiselect";

export default {
    props: {
        companyID: {
            type: String,
            required: false,
        },
        searchCriteria: {
            type: Object,
            required: false,
        },
    },
    components: { ImageUploader, Multiselect },
    async created() {
        await this.lookupGroup({ hasOptionAll: false });
        await this.getCompanyData();
    },
    data() {
        return {
            submitted: false,
            createForm: this.defaultForm(),
        };
    },
    validations() {
        return {
			createForm: {
				name: { required },
                displayName: { required },
                image: { required },
                startTime: { required },
                endTime: { required },
                visibleToEveryone: { required },
                budget: { required },
                website: { required },
                annualReportWebsite: { required },
                group: { required },
			},
		};
    },
    methods: {
        ...companyMethods,
        ...lookupMethods,
        defaultForm() {
            return {
                id: "",
                name: "",
                displayName: "",
                image: null,
                startTime: "",
                endTime: "",
                operateDescription: "",
                visibleToEveryone: null,
                budget: "",
                hashtag: [],
                website: "",
                annualReportWebsite: "",
                group: null,
            };
        },
        async getCompanyData() {
            if (this.companyID) {
                await this.getCompany({ id: this.companyID });
                this.createForm = {
                    id: this.data.id,
                    name: this.data.name,
                    displayName: this.data.displayName,
                    image: this.data.logoUrl,
                    startTime: this.data.startTime.substring(0,5),
                    endTime: this.data.endTime.substring(0,5),
                    operateDescription: this.data.operateDescription,
                    visibleToEveryone: this.data.isPrivate===true ? 1 : this.data.isPrivate===false ? 0 : null,
                    budget: this.data.budget,
                    hashtag: this.data.hashtags ? this.data.hashtags.map(tag => tag.name) : [],
                    website: this.data.website,
                    annualReportWebsite: this.data.annualReportWebsite,
                    group: this.data.groupID ? {
                        value: this.data.groupID.value,
                        text: this.data.groupID.text,
                    } : null,
                };
            }
        },
        uploadImage(image) {
            this.createForm.image = image[0]?.file;
        },
        removeImage() {
            this.createForm.image = null;
        },
        addTag(newTag) {
            const tag = newTag.trim();
            if (tag && !this.createForm.hashtag.includes(tag)) {
                this.createForm.hashtag.push(tag);
            }
        },
        requestCreate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
					...this.createForm,
                    group: this.createForm.group.value,
                }
                this.createCompany({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        requestUpdate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
					...this.createForm,
                    group: this.createForm.group.value,
                }
                if(this.createForm.image instanceof File) {
                    data.image = this.createForm.image;
                }
                this.updateCompany({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        resetForm() {
            this.createForm = this.defaultForm();
            this.submitted = false;
            this.$v.$reset();
            this.$refs.coverUploader?.clear?.();
        },
    },
    computed: {
        ...mapState(COMPANY_MODULE, ['status']),
        groupDdl() {
            return this.$store.state.lookup.groupDdl || [];
        },
        data() {
            return this.$store.state.company.details;
        },
    },
}
</script>